import { create } from 'zustand';

import { UserRole } from 'models/user.model';
import { useUserStore } from 'store/user';
import { isValidEPAMEmail } from './email-validation';
import { PermissionState } from './permission.type';

const initialState: PermissionState = {
  role: 'ADMIN',
  emails: [],
  admins: [],
  input: '',
  errorMessage: '',
};

const EMAIL_LENGTH = 72;
const DOMAIN = '@epam.com';

export const usePermissionStore = create<PermissionState>(() => ({
  ...initialState,
}));

export const updateInputValue = (value: string): void => {
  const currentUserEmail = useUserStore.getState().user?.email;
  const { admins } = usePermissionStore.getState();
  const lowercaseMorphEmails = value
    .toLowerCase()
    .replaceAll(/[,;]/g, '')
    .split(' ');

  lowercaseMorphEmails.some((lowercaseMorphEmail) => {
    const emailList = usePermissionStore.getState().emails;

    if (lowercaseMorphEmail.length > EMAIL_LENGTH) {
      usePermissionStore.setState({
        errorMessage: 'Email address is too long',
        input: lowercaseMorphEmail,
      });

      return true;
    }

    if (admins.includes(lowercaseMorphEmail)) {
      usePermissionStore.setState({
        errorMessage: 'Email address is already in the list',
        input: lowercaseMorphEmail,
      });

      return true;
    }

    if (lowercaseMorphEmail === currentUserEmail) {
      usePermissionStore.setState({
        errorMessage: 'You cannot invite yourself',
        input: lowercaseMorphEmail,
      });

      return true;
    }

    if (
      lowercaseMorphEmail.endsWith(DOMAIN) &&
      lowercaseMorphEmail.length <= EMAIL_LENGTH
    ) {
      if (isValidEPAMEmail(lowercaseMorphEmail)) {
        usePermissionStore.setState({
          emails: [...emailList, lowercaseMorphEmail],
          errorMessage: '',
          input: '',
        });

        return false;
      }
    }

    usePermissionStore.setState({
      input: lowercaseMorphEmail,
      errorMessage: value ? undefined : '',
    });

    return false;
  });
};

export const pushEmailToList = (email: string): void => {
  const currentUserEmail = useUserStore.getState().user?.email;
  const emailList = usePermissionStore.getState().emails;
  const morphEmail = email.toLowerCase().replace(/\s/g, '');
  const { errorMessage } = usePermissionStore.getState();

  if (errorMessage) {
    return usePermissionStore.setState({});
  }

  if (morphEmail.length > EMAIL_LENGTH) {
    return usePermissionStore.setState({
      errorMessage: 'Email address is too long',
      input: morphEmail,
    });
  }

  if (emailList.length > 4) {
    return usePermissionStore.setState({
      errorMessage: 'You can add up to 5 emails',
    });
  }

  if (morphEmail === currentUserEmail) {
    return usePermissionStore.setState({
      errorMessage: 'You cannot invite yourself',
      input: morphEmail,
    });
  }

  if (emailList.includes(morphEmail)) {
    return usePermissionStore.setState({
      errorMessage: 'This email is already in the list',
    });
  }

  if (morphEmail.endsWith(DOMAIN) && morphEmail.length <= EMAIL_LENGTH) {
    if (isValidEPAMEmail(morphEmail)) {
      return usePermissionStore.setState({
        emails: [...emailList, morphEmail],
        errorMessage: '',
        input: '',
      });
    }
  }

  return usePermissionStore.setState({
    errorMessage:
      'Please enter a valid EPAM email address. Example: john_doe@epam.com',
  });
};

export const revertEmailAsInput = (email: string): void => {
  const emailList = usePermissionStore.getState().emails;

  usePermissionStore.setState({
    emails: emailList.filter((e) => e !== email),
    input: email,
    errorMessage: '',
  });
};

export const removeEmailFromList = (email: string): void => {
  const emailList = usePermissionStore.getState().emails;

  usePermissionStore.setState({
    emails: emailList.filter((e) => e !== email),
    errorMessage: '',
  });
};

export const updateRole = (role: UserRole): void => {
  usePermissionStore.setState({ role });
};

export const setErrorMessage = (message: string): void => {
  usePermissionStore.setState({ errorMessage: message });
};

export const clearErrorMessage = (): void => {
  usePermissionStore.setState({ errorMessage: '' });
};

export const clearPermissionStore = (): void => {
  usePermissionStore.setState(initialState);
};
